import React from 'react';
import { connect, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { mapDispatchToProps, mapStateToProps, Props } from 'component/page/search/Categories/CategoriesTabs.selectors';
import { TabItem, TabItems, Tabs } from '@drone-js/core';
import { getCategoryItems, getFilterStateForOneValue } from 'component/page/search/SortAndFilter/Filter/filter.utils';
import { selectCmsFromBootstrap } from 'store/slice/bootstrap';
import { FilterCode } from 'gql/autogen/FilterCode';
import { StringFormatter } from '@agoda/react-handyman';
import { TabsPlaceHolder } from 'component/common/PlaceHolder/TabsPlaceHolder/TabsPlaceHolder';
import { HorizontalScrollOnlyScrollIntoViewOptions, scrollIntoViewWithSelector } from 'core/utils/domUtils';
import { getSortAndFilterQueryString } from 'component/page/search/SortAndFilter/sortAndFilterQuery.utils';
import { replaceState } from 'core-framework/window/history';
import { pathname } from 'core-framework/window/location';
import { getSelectedTab } from 'component/page/search/Categories/categories.utils';
import { FilterState } from 'component/common/Filter/Filter.types';

const CategoriesTabs: React.FC<Props> = React.memo((props) => {
    const cms = useSelector(selectCmsFromBootstrap);
    const locationSearch = useLocation().search;
    const navigation = useNavigate();
    const [currentFilter, setCurrentFilters] = React.useState(props.filter);
    const categoryFilter = getCategoryItems(cms, currentFilter.valueFilterMatrix, props.filterMatrix?.valueFilterMatrix);
    const selectedTab = getSelectedTab(categoryFilter);
    const totalTabs = categoryFilter.length;

    React.useEffect(() => {
        // Load input filters from URL into local state
        setCurrentFilters(props.filter);
    }, [props.filter]);

    React.useEffect(() => {
        if (totalTabs <= 0) return;
        setTimeout(() => {
            scrollIntoViewWithSelector(`#categories-tabs-tab-${selectedTab + 1}`, HorizontalScrollOnlyScrollIntoViewOptions);
        }, 180);
    }, [props.screenWidth, selectedTab, totalTabs]);

    const categoryItems = categoryFilter.map((item, index) => (
        <TabItem
            data-element-name="activities-categories-filter"
            data-testid="activities-categories-filter"
            data-element-index={index + 1}
            key={item.label}
            label={StringFormatter.format(item.tabLabel, item.count)}
            leadingAsset={item.asset}
        />
    ));

    const applyCategories = (categoryState: FilterState, index: number) => {
        setCurrentFilters(categoryState);
        const query = getSortAndFilterQueryString(locationSearch, props.sort, categoryState, true);
        props.setCategoriesFilter(props.sort, categoryState, index, categoryFilter[index].count);
        if (props.isNoResults && categoryFilter[index].count === 0) {
            replaceState(`${pathname()}?${query}`);
            return;
        }
        setTimeout(() => {
            navigation(`?${query}`, { replace: props.isFilterWithoutUpdatingHistory });
        }, 550);
    };

    if (categoryItems.length === 0) {
        return <TabsPlaceHolder element="categories" item={10} />;
    }

    const onCategoryTabClick = (index: number) => {
        if (index === selectedTab) return;
        if (index === 0) {
            const emptyState = getFilterStateForOneValue(FilterCode.Category, [], currentFilter);
            applyCategories(emptyState, index);
        } else {
            const selectedCategory = categoryFilter[index].values[0] as string;
            const categoryState = getFilterStateForOneValue(FilterCode.Category, [selectedCategory], currentFilter);
            applyCategories(categoryState, index);
        }
    };

    return (
        // eslint-disable-next-line @agoda/agoda-a11y/no-hardcoded-content -- @agoda/agoda-a11y/no-hardcoded-content[45860d55-dff2-4c62-b314-7b0f198aae30]
        <Tabs
            key={props.screenWidth}
            data-testid="categories-tabs-container"
            id="categories-tabs"
            label="categories-tabs"
            items={categoryItems as TabItems}
            divider={false}
            width="content-fit"
            onTabChange={onCategoryTabClick}
            selection={selectedTab}
        />
    );
});
CategoriesTabs.displayName = 'CategoriesTabs';
export default connect(mapStateToProps, mapDispatchToProps)(CategoriesTabs);
